import React, { useState, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { translate } from "@/components/IntlProviderWrapper";
import { classNames } from "@/lib/Util";
import type { ICategoryItemExt } from "./CategoryCreate";

import ConfirmationModal from "@/components/ConfirmationModal";
import ErrorComponent from "@/components/ErrorComponent";
import Loader from "@/components/Loader";

type Props = {
    items: ICategoryItemExt[];
    title?: string;
    canEdit?: boolean;
    isLoading?: boolean;
    error?: string;
    searchTerm?: string;
    onAdd: () => void;
    onEdit: (item: ICategoryItemExt) => void;
    onRemove: (item: ICategoryItemExt) => Promise<void>;
    onUndoDelete?: (item: ICategoryItemExt) => void;
    onSearchChange?: (searchTerm: string) => void;
};

/**
 * Reusable ItemsList component for displaying a list of items with CRUD operations.
 */
const CategoryItemsList = ({
    items,
    title = "Items",
    canEdit = false,
    isLoading = false,
    error = "",
    searchTerm = "",
    onAdd,
    onEdit,
    onRemove,
    onUndoDelete,
    onSearchChange,
}: Props) => {
    const [show_delete_modal, setShowDeleteModal] = useState(false);
    const [selected_item, setSelectedItem] = useState<ICategoryItemExt | null>(null);
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

    // Filter items based on search term
    const filtered_items = useMemo(() => {
        const currentSearchTerm = onSearchChange ? searchTerm : localSearchTerm;
        if (!currentSearchTerm.trim()) return items;

        const needle = currentSearchTerm.toLowerCase().trim();
        return items.filter(
            item =>
                item.name.toLowerCase().includes(needle) ||
                (item.description && item.description.toLowerCase().includes(needle)) ||
                (item.code && item.code.toLowerCase().includes(needle))
        );
    }, [items, searchTerm, localSearchTerm, onSearchChange]);

    const sorted_items = useMemo(() => {
        const only_new_items = filtered_items
            .filter(item => item.id === null)
            .sort((a, b) => a.name.localeCompare(b.name));
        const only_previous_items = filtered_items
            .filter(item => item.id !== null)
            .sort((a, b) => a.name.localeCompare(b.name));
        return [...only_previous_items, ...only_new_items];
    }, [filtered_items]);

    // Count active (non-deleted) items
    const active_items_count = useMemo(() => items.filter(item => !item.deleted).length, [items]);

    const handleOpenDeleteModal = (item: ICategoryItemExt) => {
        setSelectedItem(item);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setSelectedItem(null);
        setShowDeleteModal(false);
    };

    const onAcceptRemove = async () => {
        if (selected_item) {
            try {
                await onRemove(selected_item);
                handleCloseDeleteModal();
            } catch (err) {
                // Error handling is managed by parent component
            }
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalSearchTerm(value);
        if (onSearchChange) {
            onSearchChange(value);
        }
    };

    if (isLoading) {
        return (
            <div className="card-deck mt-3 category-items-list">
                <div className="card">
                    <div className="card-header text-uppercase bg-transparent">
                        <FormattedMessage id="Categories.items" defaultMessage={title} />
                    </div>

                    <div className="d-flex justify-content-center align-items-center">
                        <Loader />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card-deck mt-3">
            <div className="card">
                <div className="card-header text-uppercase bg-transparent">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <FormattedMessage id="Categories.items" defaultMessage={title} /> ({sorted_items.length}
                            {active_items_count !== items.length && (
                                <span className="text-muted ml-1">
                                    • {active_items_count} {translate("common.active", "active")}
                                </span>
                            )}
                            )
                        </div>
                        {canEdit && onAdd && (
                            <button
                                className="btn btn-outline-secondary btn-short"
                                onClick={onAdd}
                                aria-label={translate("common.add", "Add")}
                            >
                                <i className="fas fa-plus mr-1" aria-hidden="true"></i>
                                <FormattedMessage id="common.add" defaultMessage="Add" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Search bar */}
                <div className="card-body pb-0">
                    <div className="form-group mb-3">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={translate("common.search", "Search") + "..."}
                            value={onSearchChange ? searchTerm : localSearchTerm}
                            onChange={handleSearchChange}
                            aria-label={translate("common.search", "Search")}
                            autoComplete="off"
                        />
                    </div>
                </div>

                {error && (
                    <div className="card-body pt-0">
                        <ErrorComponent msg={error} type="error" />
                    </div>
                )}

                <div className="list-group list-group-flush">
                    {sorted_items.length === 0 ? (
                        <div className="list-group-item text-center text-muted py-4">
                            {items.length === 0 ? (
                                <>
                                    <i className="fas fa-inbox fa-2x mb-3 d-block text-muted" aria-hidden="true"></i>
                                    <p className="mb-2">{translate("common.no_items_found", "No items found")}</p>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search fa-2x mb-3 d-block text-muted" aria-hidden="true"></i>
                                    <p>{translate("common.no_search_results", "No items match your search")}</p>
                                </>
                            )}
                        </div>
                    ) : (
                        sorted_items.map((item, index) => (
                            <div
                                key={`${item.code ?? item.name}-${index}`}
                                className={classNames(
                                    "list-group-item d-flex justify-content-between align-items-start",
                                    {
                                        "text-muted": item.deleted,
                                    }
                                )}
                                role="listitem"
                            >
                                <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div
                                            className={classNames({
                                                "text-line-through": item.deleted,
                                            })}
                                        >
                                            <h6 className="mb-1 font-weight-medium">
                                                {item.name}
                                                {item.id === null ? <span className="text-danger"> *</span> : ""}
                                            </h6>
                                            {item.code && (
                                                <small className="text-muted d-block mb-1">
                                                    {translate("common.code", "Code")}:{" "}
                                                    <span className="font-weight-bold">{item.code}</span>
                                                </small>
                                            )}
                                        </div>
                                        {canEdit && (
                                            <div
                                                className="flex-shrink-0"
                                                role="group"
                                                aria-label={translate("common.actions", "Actions")}
                                            >
                                                {!item.deleted && (
                                                    <button
                                                        className="btn btn-outline-secondary btn-short px-1 ml-2"
                                                        onClick={() => onEdit(item)}
                                                        aria-label={translate("common.edit", "Edit")}
                                                        title={translate("common.edit", "Edit")}
                                                    >
                                                        <i className="fas fa-edit" aria-hidden="true" />
                                                        <span className="sr-only">
                                                            <FormattedMessage id="common.edit" defaultMessage="Edit" />
                                                        </span>
                                                    </button>
                                                )}
                                                {item.deleted ? (
                                                    <div className="d-flex align-items-center">
                                                        <small className="text-muted ml-2">
                                                            <i className="fas fa-trash-alt mr-1" aria-hidden="true" />
                                                            <FormattedMessage
                                                                id="common.to_be_deleted"
                                                                defaultMessage="To be deleted"
                                                            />
                                                        </small>
                                                        {onUndoDelete && (
                                                            <button
                                                                className="btn btn-outline-warning btn-short px-1 ml-2"
                                                                onClick={() => onUndoDelete(item)}
                                                                aria-label={translate("common.undo", "Undo")}
                                                                title={translate("common.undo", "Undo")}
                                                            >
                                                                <i className="fas fa-undo" aria-hidden="true" />
                                                                <span className="sr-only">
                                                                    <FormattedMessage
                                                                        id="common.undo"
                                                                        defaultMessage="Undo"
                                                                    />
                                                                </span>
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="btn btn-outline-danger btn-short px-1 ml-2"
                                                        onClick={() => handleOpenDeleteModal(item)}
                                                        aria-label={translate("common.remove", "Remove")}
                                                        title={translate("common.remove", "Remove")}
                                                    >
                                                        <i className="fas fa-trash-alt" aria-hidden="true" />
                                                        <span className="sr-only">
                                                            <FormattedMessage
                                                                id="common.remove"
                                                                defaultMessage="Remove"
                                                            />
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {item.description && (
                                        <p
                                            className={classNames("text-muted small mb-0", {
                                                "text-line-through": item.deleted,
                                            })}
                                        >
                                            {item.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <ConfirmationModal
                show={show_delete_modal}
                title={`${translate("Categories.delete_category_item", "Delete category item")}?`}
                accept_label={translate("common.delete", "Delete")}
                cancel_label={translate("common.cancel", "Cancel")}
                dangerous={true}
                onClose={handleCloseDeleteModal}
                onAccept={onAcceptRemove}
                onCancel={handleCloseDeleteModal}
            >
                <div>
                    {translate(
                        "Categories.are_you_sure_delete_item",
                        "Are you sure you want to delete this category item?"
                    )}
                    {selected_item && (
                        <dl className="property-list alert alert-warning mt-2 mb-0">
                            <div>
                                <dt>{translate("common.code", "Code")}</dt>
                                <dd>{selected_item.code}</dd>
                            </div>
                            <div>
                                <dt>{translate("common.name", "Name")}</dt>
                                <dd>{selected_item.name}</dd>
                            </div>
                        </dl>
                    )}
                </div>
            </ConfirmationModal>
        </div>
    );
};

export default CategoryItemsList;
